import dayjs from "dayjs";
import da from "dayjs/locale/da";
import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { attendingIds, maybeIds } from "../../api/attendance";
import { useGetEventParticipants } from "../../api/events-api";
import { useLogeMembers } from "../../api/user-api";
import { useSupabaseAuth } from "../../supabase/SupabaseContext";
import { LogeEvent } from "../../types/entities";
import { Icon } from "../dss/Icon";
import { LiveDot } from "../dss/LiveDot";
import AvatarCircle from "../Avatar";

dayjs.locale(da);

interface EventCardProps {
  event: LogeEvent;
}

const EventCard: FC<EventCardProps> = ({ event }) => {
  const { id, date, status, host, title, emoji, show_vote_link } = event;
  const { data: members } = useLogeMembers();
  const { member } = useSupabaseAuth();
  const navigate = useNavigate();
  const { data: participants } = useGetEventParticipants(id);
  const confirmed = status === "confirmed";
  // The host called this samling, so there's nothing for them to vote on.
  const isHost = member?.id === host;
  const votingOpen = show_vote_link && status === "draft" && !isHost;
  // Who's coming, yes before maybe. The host counts as coming without having
  // answered anything — see api/attendance — but only once the date is
  // actually confirmed: while it's still just a draft with a vote open, the
  // host showing up as "attending" reads as more settled than it is.
  const attendingHost = confirmed ? host : null;
  const going = [
    ...attendingIds(participants ?? [], attendingHost).map((memberId) => ({ memberId, yes: true })),
    ...maybeIds(participants ?? [], attendingHost).map((memberId) => ({ memberId, yes: false })),
  ];

  // The open vote gets its own call to action below, so the subtitle stays on
  // stating the event's status rather than saying the same thing twice.
  const subtitle = confirmed
    ? dayjs(date).format("D. MMMM YYYY")
    : status === "cancelled"
      ? "Aflyst"
      : "Dato ikke fastlagt";

  return (
    // The whole row opens the read-only details. Previously only the emoji was
    // tappable and it went to the admin edit screen, which bounced every
    // non-admin straight back to the calendar.
    //
    // The rule between rows belongs to the list, not to the card — only the
    // list knows which row is last and shouldn't have one.
    <div
      onClick={() => navigate(`/event/${id}`)}
      className="py-4 flex items-start gap-4 cursor-pointer"
    >
      <div className="w-14 flex-shrink-0">
        <div className={`font-mono text-[10px] tracking-wide font-medium ${confirmed ? "text-ink2" : "text-clay"}`}>
          {confirmed ? dayjs(date).format("MMM").toUpperCase() : "TBD"}
        </div>
        {confirmed && (
          <>
            <div className="font-display font-extrabold text-[38px] leading-[0.92] tracking-tight tabular-nums">
              {dayjs(date).format("DD")}
            </div>
            {/* The weekday is a footnote to the date, not a headline: same
                mono as the month, but a shade lighter so the number leads.
                ink2 at 80% rather than ink3 — ink3 is only 3.9:1 on paper,
                too faint for 10px type; this keeps it at 5:1. */}
            <div className="font-mono text-[10px] tracking-wide text-ink2/80 mt-1">
              {dayjs(date).format("ddd").replace(".", "").toUpperCase()}
            </div>
          </>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {/* No TBD chip here: the date column already says TBD, in clay, a
            couple of centimetres to the left. */}
        <div className="font-display text-[17px] font-bold tracking-tight uppercase truncate">{title}</div>
        <div className="font-mono text-[11px] text-ink2 mt-1 tracking-wide">
          {subtitle.toUpperCase()} · V/
          {members?.find((m) => m.id === host)?.name.toUpperCase()}
        </div>
        {going.length > 0 && (
          <div className="flex -space-x-1.5 mt-2.5">
            {going.slice(0, 7).map(({ memberId, yes }) => (
              <AvatarCircle
                key={memberId}
                size="xs"
                showName={false}
                userId={memberId}
                bgColor={yes ? "green" : "yellow"}
              />
            ))}
          </div>
        )}
        {/* Voting is the one thing a member can actually *do* from the
            calendar, so it gets a real button rather than a caption: full
            width of the row, the only saturated colour on the page, and a
            live dot pulsing next to it. */}
        {votingOpen && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/vote/${id}`);
            }}
            className="mt-3 w-full h-10 flex items-center gap-2.5 rounded-lg bg-sage dss-cta-pine px-3.5 text-paper active:opacity-80"
          >
            <LiveDot color="bg-vote-yes-soft" />
            <span className="flex-1 text-left font-display text-[12.5px] font-bold uppercase tracking-wide">
              Stem på dato
            </span>
            <Icon name="chevron-right" size={11} />
          </button>
        )}
      </div>
      <div className="text-[22px] mt-1">{emoji}</div>
    </div>
  );
};
export default EventCard;
